import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime, distinctUntilChanged, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MaterialModule } from 'src/app/material.module';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { PaginationResult } from 'src/app/models/common.response';
import { PAGE_SIZE_OPTIONS, DATE_FORMAT } from 'src/app/common/constant/constants.type';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { PharmacyService } from './pharmacy.service';
import { InventoryDialogComponent } from './inventory-dialog/inventory-dialog.component';
import { AddBatchDialogComponent } from './add-batch-dialog/add-batch-dialog.component';
import { DispenseDialogComponent } from './dispense-dialog/dispense-dialog.component';
import { StockInDialogComponent } from './stock-in-dialog/stock-in-dialog.component';
import {
  DrugInventoryItem,
  PharmacyBillListItem,
  StockInListItem,
} from 'src/app/models/pharmacy.model';

@Component({
  selector: 'app-pharmacy',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
    IndianCurrencyPipe,
  ],
  providers: [
    IndianCurrencyPipe,
    DatePipe,
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './pharmacy.component.html',
  styleUrls: ['./pharmacy.component.scss'],
})
export class PharmacyComponent implements OnInit, OnDestroy {

  selectedTabIndex = 0;
  private destroy$ = new Subject<void>();
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  maxDate = new Date();

  // --- Dispense Tab (Bill List) ---
  billFilterForm: FormGroup;
  billDataSource = new MatTableDataSource<PharmacyBillListItem>();
  billDisplayedColumns = ['patientName', 'billNo', 'billAmount', 'paidAmount', 'pendingAmount', 'billDate', 'paymentStatus'];
  billSearchCtrl = new FormControl('');
  isBillsLoading = false;
  @ViewChild('billPaginator') billPaginator: MatPaginator;

  // --- Inventory Tab ---
  inventoryDisplayedColumns: string[] = [
    'drugName', 'drugTypeName', 'sellingPrice', 'gstPercentage',
    'totalStock', 'reorderLevel', 'action'
  ];
  inventoryDataSource = new MatTableDataSource<DrugInventoryItem>();
  @ViewChild('inventoryPaginator', { static: false }) inventoryPaginator: MatPaginator;
  inventoryPagination: PaginationResult;
  inventoryCount = 0;
  inventorySearchCtrl = new FormControl();
  isInventoryLoading = false;

  // --- Stock In Tab (List) ---
  stockInFilterForm: FormGroup;
  stockInDataSource = new MatTableDataSource<StockInListItem>();
  stockInDisplayedColumns = ['createdOn', 'reference', 'drugName', 'batchNo', 'quantity', 'purchasePrice', 'sellingPrice'];
  stockInSearchCtrl = new FormControl('');
  isStockInLoading = false;
  @ViewChild('stockInPaginator') stockInPaginator: MatPaginator;

  constructor(
    private fb: FormBuilder,
    private pharmacyService: PharmacyService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.loadInventory();
    this.setupInventorySearch();
    this.initBillFilters();
    this.initStockInFilters();
    this.setupBillSearch();
    this.setupStockInSearch();
    this.loadBills();
  }

  ngAfterViewInit(): void {
    this.billDataSource.paginator = this.billPaginator;
    this.stockInDataSource.paginator = this.stockInPaginator;
  }

  // =============================================
  // DISPENSE TAB (Bill List)
  // =============================================

  private initBillFilters(): void {
    const now = new Date();
    this.billFilterForm = this.fb.group({
      startDate: [new Date(now.getFullYear(), now.getMonth(), 1)],
      endDate: [new Date()],
    });
  }

  get billStartDate(): FormControl {
    return this.billFilterForm.get('startDate') as FormControl;
  }

  get billEndDate(): FormControl {
    return this.billFilterForm.get('endDate') as FormControl;
  }

  private setupBillSearch(): void {
    this.billSearchCtrl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value: string | null) => {
        this.billDataSource.filter = (value ?? '').trim().toLowerCase();
        if (this.billDataSource.paginator) {
          this.billDataSource.paginator.firstPage();
        }
      });
  }

  loadBills(): void {
    const { startDate, endDate } = this.billFilterForm.getRawValue();
    this.isBillsLoading = true;

    this.pharmacyService.getPharmacyBills({
      startDate: this.datePipe.transform(startDate, DATE_FORMAT) ?? '',
      endDate: this.datePipe.transform(endDate, DATE_FORMAT) ?? '',
      pageNumber: 0,
      pageSize: 0,
    }).pipe(
      finalize(() => this.isBillsLoading = false),
      takeUntil(this.destroy$),
    ).subscribe((response) => {
      if (response.isSuccess) {
        this.billDataSource.data = response.result.items;
        this.billDataSource.filterPredicate = (data, filter) => {
          const search = filter.toLowerCase();
          return (data.patientName ?? '').toLowerCase().includes(search)
            || (data.billNo ?? '').toLowerCase().includes(search);
        };
        this.billDataSource.paginator = this.billPaginator;
      }
    });
  }

  openDispenseDialog(mode: 'create' | 'view', billId?: number): void {
    this.dialog.open(DispenseDialogComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: ['pharmacy-dialog', 'side-panel'],
      disableClose: true,
      data: { mode, billId },
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.loadBills();
        }
      });
  }

  clearBillFilters(): void {
    const now = new Date();
    this.billSearchCtrl.setValue('');
    this.billDataSource.filter = '';
    this.billFilterForm.patchValue({
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(),
    });
    this.loadBills();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Paid': return 'status-paid';
      case 'PartialPaid': return 'status-partial';
      case 'UnPaid': return 'status-unpaid';
      default: return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'Paid': return 'Paid';
      case 'PartialPaid': return 'Partial';
      case 'UnPaid': return 'Unpaid';
      default: return '';
    }
  }

  // =============================================
  // INVENTORY TAB
  // =============================================

  onTabChanged(index: number): void {
    this.selectedTabIndex = index;

    if (index === 0) {
      this.loadInventory();        // Inventory
    }
    else if (index === 1) {
      this.loadStockInList();    // Stock In
    }
    else if (index === 2) {
      this.loadBills();         // Dispense
    }
  }

  loadInventory(): void {
    this.isInventoryLoading = true;
    const request = {
      pageNumber: this.inventoryPagination?.currentPage || 0,
      pageSize: this.inventoryPagination?.pageSize || 20,
      isDescending: false,
      searchValue: this.inventorySearchCtrl.value || '',
    };

    this.pharmacyService.getAllInventory(request)
      .pipe(finalize(() => (this.isInventoryLoading = false)))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.inventoryDataSource.data = response.result.items;
          this.inventoryPagination = response.result.pagination as any;
          this.inventoryCount = response.result.pagination.rowCount;
        }
      });
  }

  setupInventorySearch(): void {
    this.inventorySearchCtrl.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isInventoryLoading = true;
        return this.pharmacyService.getAllInventory({
          pageNumber: 0,
          pageSize: this.inventoryPagination?.pageSize || 20,
          isDescending: false,
          searchValue: query || '',
        }).pipe(finalize(() => (this.isInventoryLoading = false)));
      })
    ).subscribe((response) => {
      if (response.isSuccess) {
        this.inventoryDataSource.data = response.result.items;
        this.inventoryCount = response.result.pagination.rowCount;
        this.inventoryPagination = response.result.pagination as any;
      }
    });
  }

  onAddInventory(): void {
    this.dialog.open(InventoryDialogComponent, {
      width: '500px',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result) this.loadInventory();
    });
  }

  onEditInventory(item: DrugInventoryItem): void {
    this.dialog.open(InventoryDialogComponent, {
      width: '500px',
      disableClose: true,
      data: { inventory: item, isEdit: true },
    }).afterClosed().subscribe((result) => {
      if (result) this.loadInventory();
    });
  }

  onAddBatch(item: DrugInventoryItem): void {
    this.dialog.open(AddBatchDialogComponent, {
      width: '500px',
      disableClose: true,
      data: { drugInventoryId: item.id, drugName: item.drugName },
    }).afterClosed().subscribe((result) => {
      if (result) this.loadInventory();
    });
  }

  onViewBatches(item: DrugInventoryItem): void {
    this.pharmacyService.getBatches(item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.dialog.open(AddBatchDialogComponent, {
            width: '600px',
            disableClose: true,
            data: {
              drugInventoryId: item.id,
              drugName: item.drugName,
              batches: response.result,
              viewOnly: true,
            },
          });
        }
      });
  }

  onInventoryPageChange(event: any): void {
    this.inventoryPagination = {
      ...this.inventoryPagination,
      currentPage: event.pageIndex,
      pageSize: event.pageSize,
    };
    this.loadInventory();
  }

  // =============================================
  // STOCK IN TAB (List)
  // =============================================

  private initStockInFilters(): void {
    const now = new Date();
    this.stockInFilterForm = this.fb.group({
      startDate: [new Date(now.getFullYear(), now.getMonth(), 1)],
      endDate: [new Date()],
    });
  }

  get stockInStartDate(): FormControl {
    return this.stockInFilterForm.get('startDate') as FormControl;
  }

  get stockInEndDate(): FormControl {
    return this.stockInFilterForm.get('endDate') as FormControl;
  }

  private setupStockInSearch(): void {
    this.stockInSearchCtrl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value: string | null) => {
        this.stockInDataSource.filter = (value ?? '').trim().toLowerCase();
        if (this.stockInDataSource.paginator) {
          this.stockInDataSource.paginator.firstPage();
        }
      });
  }

  loadStockInList(): void {
    const { startDate, endDate } = this.stockInFilterForm.getRawValue();
    this.isStockInLoading = true;

    this.pharmacyService.getStockInList({
      startDate: this.datePipe.transform(startDate, DATE_FORMAT) ?? '',
      endDate: this.datePipe.transform(endDate, DATE_FORMAT) ?? '',
      pageNumber: 0,
      pageSize: 0,
    }).pipe(
      finalize(() => this.isStockInLoading = false),
      takeUntil(this.destroy$),
    ).subscribe((response) => {
      if (response.isSuccess) {
        this.stockInDataSource.data = response.result.items;
        this.stockInDataSource.filterPredicate = (data, filter) => {
          const search = filter.toLowerCase();
          return (data.drugName ?? '').toLowerCase().includes(search)
            || (data.reference ?? '').toLowerCase().includes(search)
            || (data.batchNo ?? '').toLowerCase().includes(search);
        };
        this.stockInDataSource.paginator = this.stockInPaginator;
      }
    });
  }

  openStockInDialog(): void {
    this.dialog.open(StockInDialogComponent, {
      autoFocus: false,
      disableClose: true,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: ['pharmacy-stockin-dialog', 'side-panel'],
      data: { mode: 'create' },
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.loadStockInList();
        }
      });
  }

  clearStockInFilters(): void {
    const now = new Date();
    this.stockInSearchCtrl.setValue('');
    this.stockInDataSource.filter = '';
    this.stockInFilterForm.patchValue({
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(),
    });
    this.loadStockInList();
  }

  // =============================================
  // SHARED
  // =============================================

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
